import { useCallback, useEffect, useRef, useState } from 'react';

interface SpeechRecognitionEvent {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionInstance;

function getSpeechRecognition(): SpeechRecognitionConstructor | null {
  const w = window as any;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export function useVoiceRecognition(lang = 'en-US') {
  const [isListening, setIsListening] = useState(false);
  const [finalTranscript, setFinalTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');

  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const shouldRestartRef = useRef(false);

  const isAvailable = typeof window !== 'undefined' && !!getSpeechRecognition();

  const startListening = useCallback(() => {
    const SR = getSpeechRecognition();
    if (!SR) return;

    // Stop existing
    if (recognitionRef.current) {
      shouldRestartRef.current = false;
      recognitionRef.current.abort();
    }

    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;
    recognitionRef.current = recognition;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = '';
      let final = '';
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          final += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }
      if (final) setFinalTranscript((prev) => prev + final);
      setInterimTranscript(interim);
    };

    recognition.onerror = (event: { error: string }) => {
      // 'no-speech' and 'aborted' are non-fatal
      if (event.error === 'no-speech' || event.error === 'aborted') return;
      shouldRestartRef.current = false;
      setIsListening(false);
    };

    recognition.onend = () => {
      // Chrome kills recognition after ~60s silence — auto-restart
      if (shouldRestartRef.current) {
        try { recognition.start(); } catch { setIsListening(false); }
        return;
      }
      setIsListening(false);
    };

    shouldRestartRef.current = true;
    recognition.start();
    setIsListening(true);
  }, [lang]);

  const stopListening = useCallback(() => {
    shouldRestartRef.current = false;
    recognitionRef.current?.stop();
    setIsListening(false);
    setInterimTranscript('');
  }, []);

  const clearTranscript = useCallback(() => {
    setFinalTranscript('');
    setInterimTranscript('');
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      shouldRestartRef.current = false;
      recognitionRef.current?.abort();
    };
  }, []);

  const transcript = finalTranscript + (interimTranscript ? ' ' + interimTranscript : '');

  return {
    isListening,
    interimTranscript,
    finalTranscript,
    transcript: transcript.trim(),
    isAvailable,
    startListening,
    stopListening,
    clearTranscript,
  };
}
